import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getAttachedDeliveryLineItemsForVehicleTracking,
  getVehicleTrackingAttachedLoads,
  getProducts,
  getCategoryBusinessPartnersDropdownOptions,
  createVehicleTrackingLoadHeader,
  getAttachedDeliveriesForVehicleTracking,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as Create } from 'src/Images/icons/common/create.svg';
import 'src/style/createVehicleTracking.css';
import { Row, Col } from 'react-bootstrap';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { getPackType } from 'src/redux/Actions/VehicleTracking';
import getColorCode from 'src/utils/GetThemeColor';
import { ReactComponent as Delete } from 'src/Images/icons/common/delete.svg';

const clampToZero = (value) => Math.max(0, Number(value) || 0);
const incrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) + 1);
const decrementValue = (currentValue) =>
  clampToZero((Number(currentValue) || 0) - 1);

const getRowKey = (item) =>
  item.isNewLineItem ? item.id : item.rowKey || item.delivery_unique_number;

const getCheckedLineItemIds = (loadLineItemsList) =>
  new Set(
    loadLineItemsList
      .filter((item) => !item.isNewLineItem)
      .flatMap((deliveryObj) =>
        (deliveryObj.line_items || []).map((li) => li.delivery_line_item_id),
      ),
  );

export default function VehicleTrackingCreateLoad({ onBack, themeColor }) {
  const { vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();

  const authReducer = useSelector((state) => state.AuthReducer);
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { packTypeDataList } = vehicleTrackingReducer;

  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const organisationId = authReducer.userData.organisations?.id;

  const {
    attachedDeliveryLineItemsForVehicleTracking,
    products,
    categoryBasedBusinessPartner,
    attachedDeliveriesForVehicleTracking,
  } = VehicleTrackingOutwardReducer;

  const [activeTab, setActiveTab] = useState(1);

  const [loadLineItemsList, setLoadLineItemsList] = useState([]);
  const [lineItemErrors, setLineItemErrors] = useState({});
  const [attachedDeliveryList, setAttachedDeliveryList] = useState([]);
  const [filterFormData, setFilterFormData] = useState({
    deliveryNumber: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [businessPartnerSearchTerm, setBusinessPartnerSearchTerm] =
    useState('');

  useEffect(() => {
    if (!tenantOrganisationId) return;
    dispatch(getPackType(tenantOrganisationId));
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (!tenantOrganisationId) return;
    dispatch(getProducts(tenantOrganisationId, productSearchTerm));
  }, [dispatch, tenantOrganisationId, productSearchTerm]);

  useEffect(() => {
    if (!organisationId) return;
    dispatch(
      getCategoryBusinessPartnersDropdownOptions(
        organisationId,
        businessPartnerSearchTerm,
        '1',
      ),
    );
  }, [organisationId, dispatch, businessPartnerSearchTerm]);

  useEffect(() => {
    if (!tenantOrganisationId || !vehicleTrackingNumber) return;
    dispatch(
      getAttachedDeliveryLineItemsForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  useEffect(() => {
    if (!tenantOrganisationId || !vehicleTrackingNumber) return;

    dispatch(
      getAttachedDeliveriesForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
      ),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingNumber]);

  useEffect(() => {
    setAttachedDeliveryList(
      attachedDeliveriesForVehicleTracking?.delivery || [],
    );
  }, [attachedDeliveriesForVehicleTracking]);

  const deliveryLineItemOptions =
    attachedDeliveryLineItemsForVehicleTracking?.map((lineItem) => ({
      value: lineItem.delivery_unique_number,
      label: `${lineItem.delivery_code ?? '-'} - ${lineItem.sap_code ?? '-'}`,
      raw: lineItem,
    }));

  const productOptions = (products || []).map((product) => ({
    value: product.id,
    label: product.name,
    description: product.description,
    raw: product,
  }));

  const businessPartnerOptions = (categoryBasedBusinessPartner || []).map(
    (partner) => ({
      value: partner.id,
      label: partner.name,
      raw: partner,
    }),
  );

  const packTypeOptions = (packTypeDataList || []).map((packType) => ({
    label: packType.name,
    value: packType.id,
  }));

  const handleDeliveryDropdownOpen = (item) => {
    dispatch(
      getAttachedDeliveryLineItemsForVehicleTracking(
        tenantOrganisationId,
        vehicleTrackingNumber,
        item.productSelection?.value,
        item.businessPartnerSelection?.value,
      ),
    );
  };

  const handleDeliveryNumberSelectChange = (selectedOption) => {
    if (!selectedOption) {
      setFilterFormData({
        deliveryNumber: null,
      });
      setAttachedDeliveryList(
        attachedDeliveriesForVehicleTracking?.delivery || [],
      );
      return;
    }

    setFilterFormData({
      deliveryNumber: selectedOption,
    });
  };

  // Checked purely by comparing delivery_line_item_id (nested under each
  // delivery's line_items) against what's currently in the Load Line Items list.
  const isLineItemSelected = (deliveryObj) => {
    const checkedLineItemIds = getCheckedLineItemIds(loadLineItemsList);
    return (deliveryObj?.line_items || []).some((li) =>
      checkedLineItemIds.has(li?.delivery_line_item_id),
    );
  };

  const handleToggleDeliverySelection = (deliveryObj, checked) => {
    if (!deliveryObj) return;

    const deliveryKey = deliveryObj.delivery_unique_number;
    if (!deliveryKey) return;

    setLoadLineItemsList((prev) => {
      if (checked) {
        const alreadyAdded = prev.some(
          (item) =>
            !item.isNewLineItem && item.delivery_unique_number === deliveryKey,
        );

        if (alreadyAdded) {
          return prev;
        }

        return [
          ...prev,
          {
            ...deliveryObj,
            rowKey: deliveryKey,
            packType: deliveryObj.pack_type ?? null,
            packQuantity: clampToZero(deliveryObj.pack_quantity),
            line_items: (deliveryObj.line_items || []).map((li) => ({
              ...li,
              quantity: clampToZero(li.quantity),
            })),
          },
        ];
      }

      return prev.filter(
        (item) =>
          item.isNewLineItem || item.delivery_unique_number !== deliveryKey,
      );
    });
  };

  const handleSearchSubmit = () => {
    const allDeliveries = attachedDeliveriesForVehicleTracking?.delivery || [];

    if (!filterFormData.deliveryNumber) {
      setAttachedDeliveryList(allDeliveries);
      return;
    }

    const filtered = allDeliveries.filter(
      (delivery) =>
        delivery?.delivery_unique_number ===
        filterFormData.deliveryNumber.value,
    );

    setAttachedDeliveryList(filtered);
  };

  const updateSelectedLineItem = (rowKey, updates) => {
    setLoadLineItemsList((prev) =>
      prev.map((item) =>
        getRowKey(item) === rowKey ? { ...item, ...updates } : item,
      ),
    );
  };

  const handleAddNewLineItem = () => {
    setLoadLineItemsList((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        delivery_line_item_id: null,
        deliverySelection: null,
        productSelection: null,
        businessPartnerSelection: null,
        packType: null,
        packTypeIsPrefilled: false,
        packQuantity: 0,
        quantity: 0,
        isNewLineItem: true,
      },
    ]);
  };

  // Used by the Actions column's Delete icon in the Load Line Items table
  // to remove/deselect a row from the Attached list.
  const handleAttachedRowDelete = (item) => {
    if (item.isNewLineItem) {
      setLineItemErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[item.id];
        return updatedErrors;
      });
      setLoadLineItemsList((prev) =>
        prev.filter((selectedItem) => getRowKey(selectedItem) !== item.id),
      );
      return;
    }

    handleToggleDeliverySelection(item, false);
  };

  const handleDeliverySelect = (rowKey, selectedOption) => {
    updateSelectedLineItem(rowKey, { deliverySelection: selectedOption });
  };

  const clearLineItemFieldError = (rowKey, fieldName) => {
    setLineItemErrors((prev) => {
      if (!prev[rowKey]?.[fieldName]) return prev;

      return {
        ...prev,
        [rowKey]: {
          ...prev[rowKey],
          [fieldName]: false,
        },
      };
    });
  };

  const handleProductSelect = (rowKey, selectedOption) => {
    updateSelectedLineItem(rowKey, { productSelection: selectedOption });
    if (selectedOption?.value) {
      clearLineItemFieldError(rowKey, 'product');
      clearLineItemFieldError(rowKey, 'businessPartner');
    }
  };

  const handleBusinessPartnerSelect = (rowKey, selectedOption) => {
    updateSelectedLineItem(rowKey, {
      businessPartnerSelection: selectedOption,
    });
    if (selectedOption?.value) {
      clearLineItemFieldError(rowKey, 'businessPartner');
      clearLineItemFieldError(rowKey, 'product');
    }
  };

  const handlePackTypeChange = (rowKey, selectedOption) => {
    updateSelectedLineItem(rowKey, {
      packType: selectedOption,
      packTypeIsPrefilled: false,
    });
  };

  const handlePackQuantityChange = (rowKey, value) => {
    updateSelectedLineItem(rowKey, { packQuantity: clampToZero(value) });
  };

  const handlePackQuantityIncrement = (rowKey) => {
    setLoadLineItemsList((prev) =>
      prev.map((item) =>
        getRowKey(item) === rowKey
          ? { ...item, packQuantity: incrementValue(item.packQuantity) }
          : item,
      ),
    );
  };

  const handlePackQuantityDecrement = (rowKey) => {
    setLoadLineItemsList((prev) =>
      prev.map((item) =>
        getRowKey(item) === rowKey
          ? { ...item, packQuantity: decrementValue(item.packQuantity) }
          : item,
      ),
    );
  };

  const updateQuantity = (rowKey, value, lineItemId = null) => {
    setLoadLineItemsList((prev) =>
      prev.map((item) => {
        if (getRowKey(item) !== rowKey) return item;

        if (item.isNewLineItem) {
          return {
            ...item,
            quantity: clampToZero(value),
          };
        }

        return {
          ...item,
          line_items: (item.line_items || []).map((li) =>
            li.delivery_line_item_id === lineItemId
              ? { ...li, quantity: clampToZero(value) }
              : li,
          ),
        };
      }),
    );
  };
  const handleQuantityChange = (rowKey, value, lineItemId = null) => {
    updateQuantity(rowKey, value, lineItemId);
  };

  const handleQuantityIncrement = (rowKey, currentValue, lineItemId = null) => {
    updateQuantity(rowKey, incrementValue(currentValue), lineItemId);
  };

  const handleQuantityDecrement = (rowKey, currentValue, lineItemId = null) => {
    updateQuantity(rowKey, decrementValue(currentValue), lineItemId);
  };

  const buildLoadLineItemPayload = () =>
    loadLineItemsList.flatMap((item) => {
      const packTypeId =
        item.packType &&
        typeof item.packType === 'object' &&
        !item.packTypeIsPrefilled
          ? item.packType.value
          : null;

      if (item.isNewLineItem) {
        const payloadLineItem = {
          receiving_quantity: item.quantity,
          pack_quantity: item.packQuantity,
        };

        if (item.productSelection?.value) {
          payloadLineItem.product_id = item.productSelection.value;
        }
        if (item.businessPartnerSelection?.value) {
          payloadLineItem.business_partner_id =
            item.businessPartnerSelection.value;
        }
        if (item.deliverySelection?.raw?.delivery_line_item_id) {
          payloadLineItem.delivery_line_item_id =
            item.deliverySelection.raw.delivery_line_item_id;
        }
        if (packTypeId) {
          payloadLineItem.pack_type_id = packTypeId;
        }

        return [payloadLineItem];
      }

      return (item.line_items || [])
        .filter((li) => li.delivery_line_item_id)
        .map((li) => {
          const payloadLineItem = {
            delivery_line_item_id: li.delivery_line_item_id,
            receiving_quantity: li.quantity,
            pack_quantity: item.packQuantity,
          };
          if (li.product?.product_id) {
            payloadLineItem.product_id = li.product.product_id;
          }
          if (item.ship_to_party?.id) {
            payloadLineItem.business_partner_id = item.ship_to_party.id;
          }
          if (packTypeId) {
            payloadLineItem.pack_type_id = packTypeId;
          }

          return payloadLineItem;
        });
    });

  const validateNewLineItems = () => {
    const errors = {};

    loadLineItemsList.forEach((item) => {
      if (!item.isNewLineItem) return;

      const rowKey = getRowKey(item);
      const hasProduct = !!item.productSelection?.value;
      const hasBusinessPartner = !!item.businessPartnerSelection?.value;

      if (!hasProduct && !hasBusinessPartner) {
        errors[rowKey] = {
          product: true,
          businessPartner: true,
        };
      }
    });

    setLineItemErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (!validateNewLineItems()) {
      setActiveTab(2);
      return;
    }

    const payload = {
      tenant_organisation_id: tenantOrganisationId,
      vehicle_tracking_number: vehicleTrackingNumber,
      delivery: [],
      load_line_items: buildLoadLineItemPayload(),
    };

    setIsSubmitting(true);

    dispatch(createVehicleTrackingLoadHeader(payload))
      .then(() =>
        dispatch(
          getVehicleTrackingAttachedLoads(
            tenantOrganisationId,
            vehicleTrackingNumber,
          ),
        ),
      )
      .then(() => {
        onBack?.();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const getTabClassName = (tabKey) =>
    activeTab === tabKey
      ? `theme-pill-active-${themeColor}`
      : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey';

  const getNewLineIteSelectClassName = (rowKey, fieldName) =>
    `multi-select-input-section ${
      lineItemErrors[rowKey]?.[fieldName] ? 'form-input-error' : ''
    }`;

  const renderSearchRows = () => {
    if (!attachedDeliveryList?.length) {
      return (
        <tr>
          <td colSpan="7" className="text-center text-sm text-grey">
            No Delivery Attached Yet{' '}
          </td>
        </tr>
      );
    }

    return attachedDeliveryList.map((deliveryObj) => {
      const deliveryLineItems = deliveryObj.line_items || [];
      const tableRows = deliveryLineItems.length ? deliveryLineItems : [{}];
      const rowSpan = tableRows.length;
      const checked = isLineItemSelected(deliveryObj);

      return tableRows.map((li) => {
        const lineItemId = li?.delivery_line_item_id;

        return (
          <tr key={lineItemId || deliveryObj?.delivery_unique_number}>
            <td rowSpan={rowSpan}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) =>
                  handleToggleDeliverySelection(deliveryObj, e.target.checked)
                }
              />
            </td>
            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {deliveryObj?.delivery_code ?? '-'} -{' '}
                {deliveryObj?.sap_code ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {li?.product?.product_sap_code ?? '-'}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">
                {li?.product_description ?? '-'}
              </span>
            </td>
            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap table-ellipsis">
                {deliveryObj?.ship_to_party?.name ?? '-'}
              </span>
            </td>
            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {deliveryObj?.pack_type ?? '-'}
              </span>
            </td>
            <td rowSpan={rowSpan}>
              <span className="text-sm text-nowrap">
                {deliveryObj?.pack_quantity ?? 0}
              </span>
            </td>
            <td>
              <span className="text-sm text-nowrap">{li?.quantity ?? 0}</span>
            </td>
          </tr>
        );
      });
    });
  };

  const renderAttachedRows = () => {
    if (!loadLineItemsList?.length) {
      return (
        <tr>
          <td colSpan="7" className="text-center text-sm text-grey">
            No Load line items available.
          </td>
        </tr>
      );
    }

    return loadLineItemsList.flatMap((item) => {
      const rowKey = getRowKey(item);
      const lineItems = item.isNewLineItem ? [item] : item.line_items || [];

      return lineItems.map((li, index) => {
        const lineItemId = li?.delivery_line_item_id;

        const product = item.isNewLineItem
          ? item.productSelection
          : li?.product?.product_sap_code;

        const productDescription = item.isNewLineItem
          ? item.productSelection?.description
          : li?.product?.description;

        const businessPartner = item.isNewLineItem
          ? item.businessPartnerSelection
          : item?.ship_to_party?.name;

        const deliveryNumber = item.isNewLineItem
          ? item.deliverySelection
          : `${item?.delivery_code ?? '-'} - ${item?.sap_code ?? '-'}`;

        const quantity = item.isNewLineItem ? item.quantity : li?.quantity;

        return (
          <tr key={lineItemId || `${rowKey}-${index}`}>
            <td>
              {item.isNewLineItem ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="delivery"
                    className="multi-select-input-section"
                    classNamePrefix="react-select"
                    placeholder="Select Delivery Number"
                    options={deliveryLineItemOptions}
                    onMenuOpen={() => handleDeliveryDropdownOpen(item)}
                    value={item.deliverySelection}
                    onChange={(selectedOption) =>
                      handleDeliverySelect(rowKey, selectedOption)
                    }
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap">{deliveryNumber}</span>
              )}
            </td>

            <td>
              {item.isNewLineItem ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="product"
                    className={getNewLineIteSelectClassName(rowKey, 'product')}
                    classNamePrefix="react-select"
                    placeholder="Select Product"
                    options={productOptions}
                    value={item.productSelection}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setProductSearchTerm(inputValue);
                      }
                    }}
                    filterOption={() => true}
                    onChange={(selectedOption) => {
                      setProductSearchTerm('');
                      handleProductSelect(rowKey, selectedOption);
                    }}
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap">{product || '-'}</span>
              )}
            </td>
            <td>
              <span
                className="text-sm text-nowrap table-ellipsis"
                title={productDescription || '-'}
              >
                {productDescription || '-'}
              </span>
            </td>
            <td>
              {item.isNewLineItem ? (
                <div className="input-wrapper">
                  <CustomMultiSelect
                    isMulti={false}
                    name="business_partner"
                    className={getNewLineIteSelectClassName(
                      rowKey,
                      'businessPartner',
                    )}
                    classNamePrefix="react-select"
                    placeholder="Select Business Partner"
                    options={businessPartnerOptions}
                    value={item.businessPartnerSelection}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setBusinessPartnerSearchTerm(inputValue);
                      }
                    }}
                    filterOption={() => true}
                    onChange={(selectedOption) => {
                      setBusinessPartnerSearchTerm('');
                      handleBusinessPartnerSelect(rowKey, selectedOption);
                    }}
                    isClearable
                  />
                </div>
              ) : (
                <span className="text-sm text-nowrap table-ellipsis">
                  {businessPartner || '-'}
                </span>
              )}
            </td>

            <td>
              <div className="input-wrapper">
                <CustomMultiSelect
                  isMulti={false}
                  name="pack_type"
                  className="multi-select-input-section"
                  classNamePrefix="react-select"
                  placeholder="Select Pack Type"
                  options={packTypeOptions}
                  value={item.packType}
                  onChange={(selectedOption) =>
                    handlePackTypeChange(rowKey, selectedOption)
                  }
                  isClearable
                />
              </div>
            </td>

            <td>
              <div className="input-wrapper">
                <div className="form-input-section div-align-center div-flex-row div-space-between">
                  <div
                    role="button"
                    onClick={() => handlePackQuantityDecrement(rowKey)}
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">-</span>
                  </div>

                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={item.packQuantity ?? 0}
                    onChange={(e) =>
                      handlePackQuantityChange(rowKey, e.target.value)
                    }
                    className="input-field-none text-sm text-semi-bold"
                  />

                  <div
                    role="button"
                    onClick={() => handlePackQuantityIncrement(rowKey)}
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">+</span>
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div className="input-wrapper">
                <div className="form-input-section div-align-center div-flex-row div-space-between">
                  <div
                    role="button"
                    onClick={() =>
                      handleQuantityDecrement(
                        rowKey,
                        quantity,
                        item.isNewLineItem ? null : lineItemId,
                      )
                    }
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">-</span>
                  </div>

                  <input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    value={quantity ?? 0}
                    onChange={(e) =>
                      handleQuantityChange(
                        rowKey,
                        e.target.value,
                        item.isNewLineItem ? null : lineItemId,
                      )
                    }
                    className="input-field-none text-sm text-semi-bold"
                  />

                  <div
                    role="button"
                    onClick={() =>
                      handleQuantityIncrement(
                        rowKey,
                        quantity,
                        item.isNewLineItem ? null : lineItemId,
                      )
                    }
                    tabIndex={0}
                  >
                    <span className="text-lg text-semi-bold">+</span>
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div
                className="div-align-center div-flex-column"
                role="button"
                tabIndex={0}
                aria-label="Remove row"
                onClick={() => handleAttachedRowDelete(item)}
              >
                <Delete className="icon-size-sm" />
              </div>
            </td>
          </tr>
        );
      });
    });
  };

  return (
    <div className="div-flex-column rg-10 h-100 padding-top-10">
      {/* Header */}
      <div className="div-flex-row div-space-between div-align-center">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">Create Load</span>
          </div>
        </div>
      </div>

      <div className="div-space-between div-align-center padding-bottom-10">
        <div className="div-flex-row cg-10 text-sm">
          <button
            type="button"
            className={getTabClassName(1)}
            onClick={() => setActiveTab(1)}
          >
            Attached Deliveries
          </button>
          <button
            type="button"
            className={getTabClassName(2)}
            onClick={() => setActiveTab(2)}
          >
            Load Line Items
          </button>
        </div>
        <button
          data-testid="create-vehicle-btn"
          className={`common-btn theme-background-${themeColor}`}
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {activeTab === 1 && (
        <>
          <Row className="w-100 div-space-between">
            <Col xxl={4} xl={4} lg={4} md={6} sm={12}>
              <div className="div-flex-column">
                <span className="text-sm text-bold">Delivery Number</span>
                <div className="input-wrapper delivery-number-select">
                  <CustomMultiSelect
                    name="delivery-number"
                    isMulti={false}
                    className="multi-select-input-section margin-top-5"
                    classNamePrefix="react-select"
                    placeholder="Search Delivery Number"
                    options={deliveryLineItemOptions}
                    value={filterFormData.deliveryNumber || ''}
                    onChange={handleDeliveryNumberSelectChange}
                    isClearable
                  />

                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </div>
            </Col>

            <Col
              xxl={4}
              xl={4}
              lg={4}
              md={6}
              sm={12}
              className="d-flex justify-content-end align-items-end"
            >
              <div className="delivery-search-actions">
                <button
                  type="button"
                  className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                  onClick={handleSearchSubmit}
                >
                  Search
                </button>
              </div>
            </Col>
          </Row>

          <div className="div-flex-column rg-20 right-padding-10">
            <div className="purchase-order-line-item-card border-radius-4">
              <div className="div-space-between div-align-center px-2 py-2 border-bottom">
                <span className="text-md text-bold delivery-header">
                  Delivery
                </span>
              </div>
              <div className="load-item-table-wrapper">
                <table className="load-item-table">
                  <thead>
                    <tr>
                      <th
                        className="table-head-40"
                        aria-label="Select delivery"
                      />
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Delivery Number
                        </span>
                      </th>
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Product SAP Code
                        </span>
                      </th>
                      <th className="table-head-200">
                        <span className="text-sm text-grey text-semi-bold">
                          Product Description
                        </span>
                      </th>
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Business Partner
                        </span>
                      </th>
                      <th className="table-head-150">
                        <span className="text-sm text-grey text-semi-bold">
                          Pack Type
                        </span>
                      </th>
                      <th className="table-head-120">
                        <span className="text-sm text-grey text-semi-bold">
                          Pack Quantity
                        </span>
                      </th>
                      <th className="table-head-120">
                        <span className="text-sm text-grey text-semi-bold">
                          Quantity
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>{renderSearchRows()}</tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 2 && (
        <div className="div-flex-column rg-10 right-padding-10">
          <div className="purchase-order-line-item-card border-radius-4">
            <div className="div-space-between div-align-center px-2 py-2 border-bottom">
              <span className="text-md text-bold delivery-header">
                Attached
              </span>
            </div>
            <div className="load-item-table-wrapper">
              <table className="load-item-table">
                <thead>
                  <tr>
                    <th className="table-head-150">
                      <span className="text-sm text-grey text-semi-bold">
                        Delivery Number
                      </span>
                    </th>
                    <th className="table-head-150">
                      <span className="text-sm text-grey text-semi-bold">
                        Product SAP Code
                      </span>
                    </th>
                    <th className="table-head-200">
                      <span className="text-sm text-grey text-semi-bold">
                        Product Description
                      </span>
                    </th>
                    <th className="table-head-150">
                      <span className="text-sm text-grey text-semi-bold">
                        Business Partner
                      </span>
                    </th>
                    <th className="table-head-150">
                      <span className="text-sm text-grey text-semi-bold">
                        Pack Type
                      </span>
                    </th>
                    <th className="table-head-120">
                      <span className="text-sm text-grey text-semi-bold">
                        Pack Quantity
                      </span>
                    </th>
                    <th className="table-head-120">
                      <span className="text-sm text-grey text-semi-bold">
                        Quantity
                      </span>
                    </th>
                    <th className="table-head-50">
                      <span className="text-sm text-grey text-semi-bold">
                        Actions
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderAttachedRows()}</tbody>
              </table>
            </div>
          </div>

          <div className="div-flex-row div-flex-end">
            <button
              type="button"
              onClick={handleAddNewLineItem}
              className={`common-btn-outlined theme-border-color-${themeColor}`}
            >
              <div className="div-flex-row cg-10 div-align-center">
                <Create
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
                <span className="text-sm">Add Row</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
